import {
  Component,
  OnInit,
  OnDestroy,
  AnimationTransitionEvent,
  ElementRef,
  Output,
  Input,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';

import {
  SkySearchAdapterService
} from './search-adapter.service';

import {
  SkyMediaBreakpoints,
  SkyMediaQueryService
} from '../media-queries';

import {
  SkyResourcesService
} from '../resources';

import { Subscription } from 'rxjs/Subscription';

import {
  style,
  state,
  trigger,
  transition,
  animate
} from '@angular/animations';

const INPUT_SHOWN_STATE: string = 'inputShown';
const INPUT_HIDDEN_STATE: string = 'inputHidden';
const EXPAND_MODE_RESPONSIVE: string = 'responsive';
const EXPAND_MODE_FIT: string = 'fit';
const EXPAND_MODE_NONE: string = 'none';


export class SkySearchComponent implements OnDestroy, OnInit, OnChanges {

  
  public searchApply = new EventEmitter<string>();

  
  public searchChange = new EventEmitter<string>();

  
  public searchClear = new EventEmitter<void>();

  
  public searchText: string;

  
  public expandMode: string = EXPAND_MODE_RESPONSIVE;

  public isFullWidth: boolean = false;

  public isCollapsible: boolean = true;

  
  public get placeholderText(): string {
    if (this._placeholderText === undefined) {
      return this.resources.getString('search_placeholder');
    } else {
      return this._placeholderText;
    }
  }

  public set placeholderText(value: string) {
    this._placeholderText = value;
  }

  public inputAnimate: string = INPUT_SHOWN_STATE;
  public breakpointSubscription: Subscription;
  public searchButtonShown: boolean = false;
  public mobileSearchShown: boolean = false;
  public dismissButtonShown: boolean = false;
  public clearButtonShown: boolean = false;
  public searchInputFocused: boolean = false;

  private _placeholderText: string;

  constructor(
    private mediaQueryService: SkyMediaQueryService,
    private elRef: ElementRef,
    private searchAdapter: SkySearchAdapterService,
    private resources: SkyResourcesService,
    private changeRef: ChangeDetectorRef
  ) {}

  public ngOnInit() {
    if (this.searchShouldCollapse()) {
      this.breakpointSubscription = this.mediaQueryService.subscribe(
        (args: SkyMediaBreakpoints) => {
          this.mediaQueryCallback(args);
          this.changeRef.detectChanges();
        }
      );
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.expandModeBindingChanged(changes)) {
      switch (this.expandMode) {
        case EXPAND_MODE_NONE:
          this.isCollapsible = false;
          this.isFullWidth = false;
          break;
        case EXPAND_MODE_FIT:
          this.isCollapsible = false;
          this.isFullWidth = true;
          break;
        default:
          this.isCollapsible = true;
          this.isFullWidth = false;
          break;
      }

    }

    if (this.searchBindingChanged(changes)) {
      this.clearButtonShown = this.searchText && this.searchText !== '';
      if (this.shouldOpenInput()) {
        this.inputAnimate = INPUT_SHOWN_STATE;
      }
    }
    this.changeRef.detectChanges();
  }

  public inputFocused(isFocused: boolean) {
    this.searchInputFocused = isFocused;
  }

  public clearSearchText() {
    this.searchText = '';
    this.clearButtonShown = false;

    this.searchAdapter.focusInput(this.elRef);
    this.searchChange.emit(this.searchText);

    this.searchApply.emit(this.searchText);

    this.searchClear.emit();
  }

  public enterPress(event: KeyboardEvent, searchText: string) {
    if (event.which === 13) {
      this.applySearchText(searchText);
    }
  }

  public applySearchText(searchText: string) {
    if (searchText !== this.searchText) {
      this.searchText = searchText;
    }
    this.clearButtonShown = searchText && searchText !== '';
    if (searchText && searchText !== '') {
      this.searchAdapter.selectInput(this.elRef);
    }

    this.searchApply.emit(searchText);
  }

  public searchTextChanged(searchText: string) {
    this.searchText = searchText;
    this.searchChange.emit(searchText);
  }

  public toggleSearchInput(showInput: boolean) {
    if (this.searchShouldCollapse()) {
      if (showInput) {
        this.inputAnimate = INPUT_SHOWN_STATE;
      } else {
        this.inputAnimate = INPUT_HIDDEN_STATE;
      }
    }
  }

  public inputAnimationStart(event: AnimationTransitionEvent) {
    if (this.searchShouldCollapse()) {
      this.searchAdapter.startInputAnimation(this.elRef);

      if (event.toState === INPUT_SHOWN_STATE
        && this.mediaQueryService.current === SkyMediaBreakpoints.xs) {
        this.mobileSearchShown = true;
        this.searchButtonShown = false;

      }

    }
  }

  public inputAnimationEnd(event: AnimationTransitionEvent) {
    if (this.searchShouldCollapse()) {
      this.searchAdapter.endInputAnimation(this.elRef);

      this.searchButtonShown = event.toState === INPUT_HIDDEN_STATE
        && this.mediaQueryService.current === SkyMediaBreakpoints.xs;

      if ((event.toState === INPUT_HIDDEN_STATE
        && this.mediaQueryService.current === SkyMediaBreakpoints.xs)
        || this.mediaQueryService.current !== SkyMediaBreakpoints.xs) {
        this.mobileSearchShown = false;
      }
    }
  }

  public ngOnDestroy() {
    if (this.breakpointSubscription) {
      this.breakpointSubscription.unsubscribe();
    }
  }
  private searchBindingChanged(changes: SimpleChanges) {
    return changes['searchText'] &&
      changes['searchText'].previousValue !== changes['searchText'].currentValue;
  }

  private expandModeBindingChanged(changes: SimpleChanges) {
    return changes['expandMode'] &&
      changes['expandMode'].previousValue !== changes['expandMode'].currentValue;
  }

  private shouldOpenInput() {
    return this.searchText !== '' &&
      this.mediaQueryService.current === SkyMediaBreakpoints.xs && this.searchShouldCollapse();
  }

  private mediaQueryCallback(args: SkyMediaBreakpoints) {
    if (this.searchShouldCollapse()) {
      if (args === SkyMediaBreakpoints.xs) {
        this.inputAnimate = INPUT_HIDDEN_STATE;
      } else if (this.inputAnimate !== INPUT_SHOWN_STATE) {
        this.inputAnimate = INPUT_SHOWN_STATE;
      } else {
        this.mobileSearchShown = false;
      }
    }
    this.changeRef.markForCheck();
  }

  private searchShouldCollapse() {
    return (this.isCollapsible || this.isCollapsible === undefined) && this.isFullWidth !== true;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-search',
  template: `<div class="sky-search-container">
  <div
    class="sky-search-button-container">
    <button
      type="button"
      [ngClass]="{'sky-search-btn-open-applied': clearButtonShown}"
      [hidden]="!searchButtonShown"
      class="sky-btn sky-btn-default sky-search-btn-open"
      [attr.title]="'search_open' | skyResources"
      (click)="toggleSearchInput(true)">
      <i class="fa fa-search fa-lg"></i>
    </button>
  </div>
  <div
    [hidden]="searchButtonShown"
    [ngClass]="{'sky-search-dismiss-absolute': mobileSearchShown || isFullWidth }"
    class="sky-search-dismiss-container">
    <div class="sky-search-item-input">
      <div
        [ngClass]="{'sky-search-input-focused sky-rounded-corners': searchInputFocused}"
        [@inputState]="inputAnimate"
        (@inputState.start)="inputAnimationStart($event)"
        (@inputState.done)="inputAnimationEnd($event)"
        class="sky-search-input-container sky-input-group">
        <input
          type="text"
          class="sky-form-control sky-search-input sky-rounded-corners"
          [ngModel]="searchText"
          (ngModelChange)="searchTextChanged($event)"
          (keyup)="enterPress($event, searchText)"
          (focus)="inputFocused(true)"
          (blur)="inputFocused(false)"
          [attr.aria-label]="'search_label' | skyResources"
          [attr.placeholder]="placeholderText" />
        <span
          class="sky-input-group-btn sky-input-group-clear"
          [hidden]="!clearButtonShown">
          <button
            tabindex="-1"
            aria-hidden="true"
            type="button"
            class="sky-btn sky-btn-default sky-search-btn sky-search-btn-clear"
            (click)="clearSearchText()">
            <i class="fa fa-times"></i>
          </button>
        </span>
        <span
          class="sky-input-group-btn">
          <button
            type="button"
            class="sky-btn sky-btn-default sky-search-btn sky-search-btn-apply"
            (click)="applySearchText(searchText)"
            [attr.aria-label]="'search_label' | skyResources" >
            <i class="fa fa-search fa-lg"></i>
          </button>
        </span>
      </div>
    </div>
    <div class="sky-search-item-dismiss">
      <button
        *ngIf="mobileSearchShown"
        type="button"
        [attr.title]="'search_dismiss' | skyResources"
        class="sky-btn sky-btn-secondary sky-search-btn-dismiss"
        (click)="toggleSearchInput(false)">
        <i class="fa fa-chevron-circle-left fa-lg">
        </i>
      </button>
    </div>
  </div>
</div>
`,
  styles: [`.sky-search-input {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0 none;
  /**
   * Prevents the IE clear icon from displaying in the input
   * because our search control already includes a clear icon.
   */
}

.sky-search-input:focus {
  border: 0 none;
  box-shadow: none;
  outline: none;
}

.sky-search-input::-ms-clear {
  display: none;
}

.sky-search-btn {
  border-left: 0 none;
}

.sky-search-btn:hover, .sky-search-btn:focus {
  background-color: #ffffff;
}

.sky-search-btn-apply {
  padding-left: 6px;
  border-top: 1px solid #cdcfd2;
  border-bottom: 1px solid #cdcfd2;
  border-right: 1px solid #cdcfd2;
}

.sky-search-btn-clear {
  border-right: 0 none;
  padding-right: 6px;
  border-top: 1px solid #cdcfd2;
  border-bottom: 1px solid #cdcfd2;
}

.sky-search-input-focused {
  box-shadow: 0 0 8px rgba(0, 180, 241, 0.6);
  border: 1px solid #00b4f1;
  outline: none;
}

.sky-search-input-focused .sky-search-btn {
  border-color: transparent;
  border-top: 0 none;
  border-bottom: 0 none;
}

.sky-search-input-focused .sky-search-btn-apply {
  border-right: 0 none;
}

/**
 * Mobile styles
 */
@media (min-width: 768px) {
  .sky-search-input-container {
    min-width: 300px !important;
  }
}

/**
 * On iOS, the input will zoom when focused,
 * setting the font-size prevents this.
 */
@media (max-width: 767px) {
  .sky-search-input,
  .sky-search-btn-apply,
  .sky-search-btn-clear {
    font-size: 15px;
  }
}

.sky-search-btn-open-applied.sky-search-btn-open, .sky-search-btn-open-applied.sky-search-btn-open:hover {
  color: #71bf43;
  border: 2px solid #71bf43;
  padding: 5px 11px;
}

.sky-search-dismiss-container {
  display: flex;
}

.sky-search-item-dismiss {
  flex-shrink: 0;
  display: flex;
}

.sky-search-item-dismiss > .sky-btn {
  border-color: transparent;
}

.sky-search-item-input {
  flex-grow: 1;
  display: flex;
}

.sky-search-dismiss-absolute {
  position: absolute;
  background-color: #ffffff;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  padding: 5px;
}
`],
  animations: [
    trigger('inputState', [
      state(INPUT_HIDDEN_STATE,
        style({
          opacity: 0,
          width: 0
      })),
      state(INPUT_SHOWN_STATE,
        style({
          opacity: 1,
          width: '100%'
      })),
      transition('* <=> *', animate('150ms'))
    ])
  ],
  providers: [
    SkySearchAdapterService,
    SkyResourcesService
  ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: SkyMediaQueryService, },
{type: ElementRef, },
{type: SkySearchAdapterService, },
{type: SkyResourcesService, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'searchApply': [{ type: Output },],
'searchChange': [{ type: Output },],
'searchClear': [{ type: Output },],
'searchText': [{ type: Input },],
'expandMode': [{ type: Input },],
'placeholderText': [{ type: Input },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
